import { ActionReducerMapBuilder, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import Kemu from '@kemu-io/kemu-core';
import { findRecipe } from '@kemu-io/kemu-core/common/recipeCache';
import { Recipe } from '@kemu-io/kemu-core/types';
import { DEFAULT_THING_ID, DEFAULT_THING_VERSION } from '@kemu-io/kemu-core/common/constants';
import {
  openBlockAction,
  setExecutionModeState,
  setEditingSnapshot,
  setEditingModeWidgetIds,
  WorkspaceState,
} from '@src/features/Workspace/workspaceSlice';
import { RootState } from '@src/app/store';

/**
 * Extracts all widget IDs from a recipe in the format `thingId:widgetId`.
 * @param recipe the recipe to extract widget IDs from.
 * @returns an array of composite widget IDs.
 */
const extractWidgetIdsFromRecipe = (recipe: Recipe): string[] => {
  const widgetIds: string[] = [];
  for (const thingId of Object.keys(recipe.blocks || {})) {
    for (const widgetId of Object.keys(recipe.blocks[thingId]?.gates || {})) {
      widgetIds.push(`${thingId}:${widgetId}`);
    }
  }
  return widgetIds;
};

/**
 * Deep clones a cached recipe object while preserving the `storage` and
 * `globalVariables` references to avoid duplicating potentially large binary buffers.
 * @param recipe the recipe to clone.
 * @returns a deep clone of the recipe.
 */
const cloneRecipeForSnapshot = (recipe: Recipe): Recipe => {
  const { storage, globalVariables, ...storageLess } = recipe ?? {};
  const cloned = JSON.parse(JSON.stringify(storageLess));
  if (storage) {
    cloned.storage = storage;
  }
  if (globalVariables) {
    cloned.globalVariables = globalVariables;
  }
  return cloned;
};

type EnterExecutionModeResult = {
  success: boolean;
};

type EnterExecutionModeOptions = {
  recipePoolId: string;
};

/**
 * Async thunk for entering execution mode.
 * Clones the current recipe from the cache and stores it as the editing snapshot.
 * Also extracts and stores all widget IDs to track which widgets existed before Run mode.
 */
export const enterExecutionModeAction = createAsyncThunk<EnterExecutionModeResult, EnterExecutionModeOptions>(
  '/Workspace/enterExecutionMode',
  async (options, thunkAPI) => {
    const { recipePoolId } = options;

    // Get the current recipe from the cache
    const cachedRecipe = findRecipe(recipePoolId);
    if (!cachedRecipe) {
      throw new Error(`Recipe [${recipePoolId}] not found in cache`);
    }

    // Clone the recipe to create an editing snapshot
    const snapshot = cloneRecipeForSnapshot(cachedRecipe);

    // Extract widget IDs that exist before entering run mode
    const widgetIds = extractWidgetIdsFromRecipe(cachedRecipe);

    // Store the snapshot and widget IDs in Redux state
    thunkAPI.dispatch(setEditingSnapshot(snapshot));
    thunkAPI.dispatch(setEditingModeWidgetIds(widgetIds));
    thunkAPI.dispatch(setExecutionModeState(true));

    return { success: true };
  }
);

type ExitExecutionModeResult = {
  success: boolean;
};

type ExitExecutionModeOptions = {
  recipePoolId: string;
};

/**
 * Async thunk for exiting execution mode.
 * Terminates widgets added during run mode, requests abort of all processing,
 * waits for completion, then restores the recipe from the editing snapshot.
 */
export const exitExecutionModeAction = createAsyncThunk<ExitExecutionModeResult, ExitExecutionModeOptions>(
  '/Workspace/exitExecutionMode',
  async (options, thunkAPI) => {
    const { recipePoolId } = options;
    const state = thunkAPI.getState() as RootState;
    const editingSnapshot = state.workspace.editingSnapshot;
    const editingModeWidgetIds = state.workspace.editingModeWidgetIds;

    if (!editingSnapshot) {
      throw new Error('No editing snapshot found. Cannot exit execution mode.');
    }

    // Request abort and wait for all executions to complete
    await Kemu.requestAbortAndWait();

    // Find and terminate widgets that were added during run mode
    const currentRecipe = findRecipe(recipePoolId);
    if (currentRecipe && editingModeWidgetIds) {
      const editingModeWidgetIdsSet = new Set(editingModeWidgetIds);
      const currentWidgetIds = extractWidgetIdsFromRecipe(currentRecipe);

      // Find widgets added during run mode (exist now but didn't exist before)
      const widgetsAddedDuringRunMode = currentWidgetIds.filter(
        (id) => !editingModeWidgetIdsSet.has(id)
      );

      // Terminate widgets added during run mode
      for (const compositeId of widgetsAddedDuringRunMode) {
        const [thingId, widgetId] = compositeId.split(':');
        try {
          await Kemu.terminateWidget(recipePoolId, thingId, widgetId);
        } catch (e) {
          console.warn(`Failed to terminate widget "${widgetId}" added during run mode:`, e);
        }
      }
    }

    // Replace the recipe in the cache with the stored snapshot
    const success = Kemu.replaceRecipe(recipePoolId, editingSnapshot);
    if (!success) {
      throw new Error(`Failed to replace recipe [${recipePoolId}]`);
    }

    // Trigger state change events for all widgets so UI components update
    Kemu.triggerAllWidgetStateEvents(recipePoolId);

    // Clear execution mode state
    thunkAPI.dispatch(setEditingSnapshot(null));
    thunkAPI.dispatch(setEditingModeWidgetIds(null));
    thunkAPI.dispatch(setExecutionModeState(false));

    // Refresh the logicMapper widget map by re-dispatching openBlockAction
    thunkAPI.dispatch(openBlockAction({
      DbId: DEFAULT_THING_ID,
      recipeId: DEFAULT_THING_ID,
      recipePoolId,
      title: '',
      version: DEFAULT_THING_VERSION,
    }));

    return { success: true };
  }
);

type PreserveWidgetChangesResult = {
  success: boolean;
  /** Number of widgets that had their state preserved */
  preservedCount: number;
};

type PreserveWidgetChangesOptions = {
  /** The recipe pool id */
  recipePoolId: string;
  /** The thing id containing the widgets */
  thingId: string;
  /** Array of widget IDs to preserve changes for */
  widgetIds: string[];
};

/**
 * Async thunk for preserving widget changes made during execution mode.
 * Updates the editing snapshot with current widget states, positions, and connections
 * so they persist when exiting execution mode.
 * For new widgets (added during execution mode), adds the entire widget to the snapshot.
 */
export const preserveWidgetChangesAction = createAsyncThunk<PreserveWidgetChangesResult, PreserveWidgetChangesOptions>(
  '/Workspace/preserveWidgetChanges',
  async (options, thunkAPI) => {
    const { recipePoolId, thingId, widgetIds } = options;
    const state = thunkAPI.getState() as RootState;
    const editingSnapshot = state.workspace.editingSnapshot;
    const editingModeWidgetIds = state.workspace.editingModeWidgetIds;

    if (!editingSnapshot) {
      throw new Error('No editing snapshot found. Cannot preserve widget changes.');
    }

    // Get the current recipe from the cache
    const currentRecipe = findRecipe(recipePoolId);
    if (!currentRecipe) {
      throw new Error(`Recipe [${recipePoolId}] not found in cache`);
    }

    const editingModeWidgetIdsSet = new Set(editingModeWidgetIds || []);
    const newWidgetIds: string[] = [];
    let preservedCount = 0;

    // Deep clone the snapshot to avoid mutating Redux state directly
    const updatedSnapshot = cloneRecipeForSnapshot(editingSnapshot);

    // Ensure the thing exists in the snapshot
    if (!updatedSnapshot.blocks[thingId]) {
      updatedSnapshot.blocks[thingId] = {
        ...currentRecipe.blocks[thingId],
        gates: {},
      };
    }

    const allGates = currentRecipe.blocks[thingId]?.gates || {};

    // When a group widget is preserved, its member widgets must also be preserved.
    // Without this, members keep their pre-Execute-Mode groupId (undefined) in the snapshot,
    // so the group appears empty after returning to Compose Mode.
    const expandedWidgetIds = new Set(widgetIds);
    const groupQueue = [...widgetIds];
    while (groupQueue.length > 0) {
      const parentId = groupQueue.shift()!;
      for (const [gateId, gate] of Object.entries(allGates)) {
        if (gate.groupId === parentId && !expandedWidgetIds.has(gateId)) {
          expandedWidgetIds.add(gateId);
          groupQueue.push(gateId);
        }
      }
    }

    for (const widgetId of expandedWidgetIds) {
      const compositeId = `${thingId}:${widgetId}`;
      const currentWidget = currentRecipe.blocks[thingId]?.gates[widgetId];

      if (!currentWidget) {
        console.warn(`Widget "${widgetId}" not found in current recipe`);
        continue;
      }

      const isNew = !editingModeWidgetIdsSet.has(compositeId);

      if (isNew) {
        // For new widgets, add the entire widget to the snapshot
        updatedSnapshot.blocks[thingId].gates[widgetId] = JSON.parse(JSON.stringify(currentWidget));
        newWidgetIds.push(compositeId);
        preservedCount++;
      } else {
        // For existing widgets, update the state, canvas (position), and children (connections) in the snapshot
        if (updatedSnapshot.blocks[thingId]?.gates[widgetId]) {
          updatedSnapshot.blocks[thingId].gates[widgetId].state = JSON.parse(
            JSON.stringify(currentWidget.state)
          );
          // Also preserve canvas position if it exists
          if (currentWidget.canvas) {
            updatedSnapshot.blocks[thingId].gates[widgetId].canvas = JSON.parse(
              JSON.stringify(currentWidget.canvas)
            );
          }
          // Also preserve children (connections)
          updatedSnapshot.blocks[thingId].gates[widgetId].children = JSON.parse(
            JSON.stringify(currentWidget.children || [])
          );
          // Also preserve groupId — grouping in Execute Mode changes this field on
          // existing widgets (e.g. B.groupId becomes D), but the original code never synced it
          updatedSnapshot.blocks[thingId].gates[widgetId].groupId = currentWidget.groupId;
          preservedCount++;
        }
      }
    }

    // When a group is created, the grouping logic mutates the children of external
    // parent widgets directly in the live recipe (e.g. A→B becomes A→D). Since A itself is
    // never in the preserve list, the snapshot still holds A's old connection to B, so A
    // appears disconnected from D after returning to Compose Mode.
    // We only check snapshot gates (pre-Execute-Mode widgets) because only they could have
    // had a connection to B before the group existed.
    const snapshotGates = updatedSnapshot.blocks[thingId]?.gates || {};
    for (const [widgetId, snapshotGate] of Object.entries(snapshotGates)) {
      if (expandedWidgetIds.has(widgetId)) continue;
      const liveChildren = allGates[widgetId]?.children || [];
      if (liveChildren.some(child => expandedWidgetIds.has(child.childId))) {
        snapshotGate.children = JSON.parse(JSON.stringify(liveChildren));
      }
    }

    // Update the editing snapshot with the preserved changes
    thunkAPI.dispatch(setEditingSnapshot(updatedSnapshot));

    // Add new widget IDs to the tracking list so they are no longer considered "new"
    if (newWidgetIds.length > 0 && editingModeWidgetIds) {
      const updatedWidgetIds = [...editingModeWidgetIds, ...newWidgetIds];
      thunkAPI.dispatch(setEditingModeWidgetIds(updatedWidgetIds));
    }

    return { success: true, preservedCount };
  }
);

/**
 * Adds the execution mode reducers to the workspace slice.
 * @param builder the action reducer map builder.
 */
export const executionModeReducer = (builder: ActionReducerMapBuilder<WorkspaceState>): void => {
  // Enter execution mode
  builder.addCase(enterExecutionModeAction.fulfilled, (state, _action: PayloadAction<EnterExecutionModeResult>) => {
    // State is already updated via setExecutionModeState and setEditingSnapshot dispatches
  });

  builder.addCase(enterExecutionModeAction.rejected, (state, action) => {
    console.error('Error entering execution mode:', action.error);
    state.executionMode = false;
    state.editingSnapshot = null;
    state.editingModeWidgetIds = null;
  });

  builder.addCase(enterExecutionModeAction.pending, (_state) => {
    // Optionally handle pending state
  });

  // Exit execution mode
  builder.addCase(exitExecutionModeAction.fulfilled, (state, _action: PayloadAction<ExitExecutionModeResult>) => {
    // State is already updated via setExecutionModeState and setEditingSnapshot dispatches
  });

  builder.addCase(exitExecutionModeAction.rejected, (state, action) => {
    console.error('Error exiting execution mode:', action.error);
    // Keep current state on error, user may need to try again
  });

  builder.addCase(exitExecutionModeAction.pending, (_state) => {
    // Optionally handle pending state
  });

  // Preserve widget changes
  builder.addCase(preserveWidgetChangesAction.fulfilled, (_state, _action: PayloadAction<PreserveWidgetChangesResult>) => {
    // State is already updated via setEditingSnapshot and setEditingModeWidgetIds dispatches
  });

  builder.addCase(preserveWidgetChangesAction.rejected, (_state, action) => {
    console.error('Error preserving widget changes:', action.error);
  });

  builder.addCase(preserveWidgetChangesAction.pending, (_state) => {
    // Optionally handle pending state
  });
};

export default {
  enterExecutionModeAction,
  exitExecutionModeAction,
  preserveWidgetChangesAction,
  executionModeReducer,
};
